import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefObject,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

interface Option {
  value: string | number;
  label: ReactNode;
  renderOption?: (option: Option) => ReactNode;
  renderButton?: (option: Option) => ReactNode;
}

// Keyboard magic hook! 🎹✨
export function useSelectKeyboardNavigation({
  options,
  isOpen,
  onChange,
  closeDropdown,
  buttonRef,
  dropdownRef,
  opensUpward,
  value,
}: {
  options: Option[];
  isOpen: boolean;
  onChange: (value: string | number) => void;
  closeDropdown: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
  dropdownRef: RefObject<HTMLDivElement>;
  opensUpward: boolean; // Need to know which way it opened!
  value?: string | number;
}) {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const optionsRef = useRef<Array<HTMLButtonElement | null>>([]);

  // Simplified ref management spell!
  const getOptionRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      optionsRef.current[index] = el;
    },
    [], // No dependencies needed here, ze~!
  );

  const focusOption = useCallback(
    (index: number) => {
      if (index >= 0 && index < options.length && optionsRef.current[index]) {
        optionsRef.current[index]?.focus();
        setFocusedIndex(index);
        // Scroll into view smoothly~
        optionsRef.current[index]?.scrollIntoView({
          block: 'nearest',
        });
      }
    },
    [options.length],
  );

  const handleDropdownKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!isOpen || options.length === 0) return;
      let newIndex = focusedIndex;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          newIndex = focusedIndex >= options.length - 1 ? 0 : focusedIndex + 1;
          focusOption(newIndex);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = focusedIndex <= 0 ? options.length - 1 : focusedIndex - 1;
          focusOption(newIndex);
          break;
        case 'Enter':
        case ' ': // Spacebar selects too!
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < options.length) {
            onChange(options[focusedIndex].value);
            closeDropdown();
            buttonRef.current?.focus(); // Focus back on the button!
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeDropdown();
          buttonRef.current?.focus();
          break;
        case 'Home':
          event.preventDefault();
          focusOption(0);
          break;
        case 'End':
          event.preventDefault();
          focusOption(options.length - 1);
          break;
        case 'Tab':
          // Allow tabbing out, which should close the dropdown naturally
          closeDropdown();
          break;
        default:
          // Maybe add typeahead later? Nah, too much work, ze~!
          break;
      }
    },
    [isOpen, focusedIndex, options, onChange, closeDropdown, focusOption, buttonRef],
  );

  const handleButtonKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      // Open dropdown with arrows if closed
      if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        // Let the main component handle opening, focus will be handled in useEffect
      }
      // Allow Enter/Space to toggle open/close via the button's onClick
    },
    [isOpen], // Only depends on isOpen
  );

  // Effect to handle focus when dropdown opens/closes
  useEffect(() => {
    let focusTimerId: NodeJS.Timeout | null = null;

    if (isOpen) {
      // Reset refs array size just in case options changed while closed
      optionsRef.current = optionsRef.current.slice(0, options.length);

      focusTimerId = setTimeout(() => {
        dropdownRef.current?.focus(); // Focus container first for overall context

        let focusTargetIndex = options.findIndex((opt) => opt.value === value);

        // If no value selected or value not in options, default based on open direction
        if (focusTargetIndex === -1 && options.length > 0) {
          focusTargetIndex = opensUpward ? options.length - 1 : 0;
        }

        if (focusTargetIndex !== -1) {
          focusOption(focusTargetIndex); // Set initial focus
        } else {
          setFocusedIndex(-1); // No item to focus
        }
      }, 50); // Small delay for rendering/positioning
    } else {
      setFocusedIndex(-1); // Reset focus when closed
    }

    return () => {
      if (focusTimerId) clearTimeout(focusTimerId);
    };
    // Depend on isOpen, options, value, opensUpward, focusOption, dropdownRef
  }, [isOpen, options, value, opensUpward, focusOption, dropdownRef]);

  return {
    focusedIndex,
    optionsRef, // Still needed if external access is required, but mostly internal now
    getOptionRef, // The new way to set refs!
    handleDropdownKeyDown,
    handleButtonKeyDown,
    focusOption, // Might be useful externally? Maybe not.
  };
}
